import { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { Spinner } from '@components';
import { AccountType } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import {
  useLazyAppConfigQuery,
  useLazyGetOrganizationListQuery
} from '@features/authentication/queries/AuthQuery';
import { useLazyGetBranchConfigQuery } from '@features/branch-management/queries';
import { setMemberCustomFields } from '@features/branch-management/slices/branchConfigSlice';
import {
  useLazyGetOrganizationConfigQuery,
  useLazyGetOrgFeatureFlagsConfigQuery
} from '@features/group-management/queries/GroupQuery';
import {
  setAccountType,
  setAppConfig,
  setCurrentOrg,
  setOrgConfig,
  setOrgFeatureFlags
} from '@layouts/main-header/userSlice';
import { getLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

import AccessGuard from '../access-guard/AccessGuard';
import ApprovalGuard from '../approval-guard/ApprovalGuard';
import MobileLayout from '../mobile-layout/MobileLayout';

const BlankLayout = () => {
  const dispatch = useDispatch();
  const token = getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const { currentOrg, orgConfig, appConfig } = useSelector(
    (state: any) => state.user
  );
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);

  const [getOrgList] = useLazyGetOrganizationListQuery();
  const [getOrgConfig] = useLazyGetOrganizationConfigQuery();
  const [getBranchConfig] = useLazyGetBranchConfigQuery();
  const [getAppConfigQuery] = useLazyAppConfigQuery();
  const [getOrgFeatureFlags] = useLazyGetOrgFeatureFlagsConfigQuery();

  const getOrgFeatureFlagsData = async () => {
    const payload = {
      organizationId: currentOrg?.id || organizationId
    };
    const resp = await getOrgFeatureFlags(payload);
    if ('data' in resp) {
      dispatch(setOrgFeatureFlags(resp.data));
    }
  };

  useEffect(() => {
    if (!token || currentOrg?.id) return;

    const fetchOrgDetails = async () => {
      const resp = await getOrgList(null, false);
      if ('data' in resp) {
        const org = resp.data?.find((o: Org) => o.id === organizationId);
        if (org) {
          dispatch(setCurrentOrg(org));
          dispatch(setAccountType({ type: org?.type }));
        }
      }
    };

    fetchOrgDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organizationId]);

  useEffect(() => {
    if (!token || !organizationId) return;

    if (!orgConfig) {
      const fetchOrgConfig = async () => {
        const resp = await getOrgConfig({ organizationId });
        if ('data' in resp) {
          dispatch(setOrgConfig(resp.data));
        }
      };
      fetchOrgConfig();
    }

    if (branchId && currentOrg?.type === AccountType.ORGANIZATION) {
      const fetchBranchConfig = async () => {
        const branchConfigResp = await getBranchConfig({
          organizationId,
          branchId
        });
        if ('data' in branchConfigResp) {
          dispatch(
            setMemberCustomFields(
              branchConfigResp.data?.member_custom_fields || []
            )
          );
        }
      };
      fetchBranchConfig();
    }

    if (!appConfig) {
      const fetchAppConfig = async () => {
        const resp = await getAppConfigQuery(null);
        if ('data' in resp) {
          dispatch(setAppConfig(resp.data));
        }
      };
      fetchAppConfig();
    }

    getOrgFeatureFlagsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organizationId]);

  return (
    <AccessGuard>
      <div className="z-100 hidden h-screen w-screen overflow-hidden mobile:flex">
        <MobileLayout />
      </div>
      <div className="flex h-screen w-screen overflow-hidden mobile:hidden">
        <Suspense
          fallback={
            <div className="absolute left-1/2 top-1/2 flex size-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <ApprovalGuard>
            <Outlet />
          </ApprovalGuard>
        </Suspense>
      </div>
    </AccessGuard>
  );
};

export default BlankLayout;
